import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';

export type CompanyDialogMode = 'create' | 'view' | 'edit';

export interface CompanyFormData {
  id?: string;
  code: string;
  name: string;
  status?: 'ACTIVE' | 'INACTIVE';
  registeredBusinessName?: string;
  legalPerson?: string;
  businessLicense?: string;
}

export interface CompanyDialogResult {
  submit: boolean;
  data: CompanyFormData;
}

export interface CompanyDialogError {
  message: string;
}

@Component({
  selector: 'app-company-form-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    AppButtonComponent,
    InputTextModule,
    TextareaModule,
    SelectModule,
  ],
  templateUrl: './company-form-dialog.html',
  styleUrl: './company-form-dialog.css',
})
export class CompanyFormDialogComponent {
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly config = inject(DynamicDialogConfig);
  private readonly translateService = inject(TranslateService);

  showErrors = signal(false);
  submitting = signal(false);
  serverError = signal<CompanyDialogError | null>(null);
  readonly mode: CompanyDialogMode = this.config.data?.mode ?? 'create';

  get isViewMode(): boolean {
    return this.mode === 'view';
  }

  get isEditMode(): boolean {
    return this.mode === 'edit';
  }

  get isCreateMode(): boolean {
    return this.mode === 'create';
  }

  readonly maxFieldLength = 255;

  formData: CompanyFormData = {
    code: '',
    name: '',
    status: 'ACTIVE',
    registeredBusinessName: '',
    legalPerson: '',
    businessLicense: '',
  };

  constructor() {
    if (this.config.data?.company) {
      this.formData = {
        ...this.formData,
        ...this.config.data.company,
      };
    }
  }

  get statusOptions() {
    return [
      {
        label: this.translateService.instant(
          'organizational.company.status.active'
        ),
        value: 'ACTIVE',
      },
      {
        label: this.translateService.instant(
          'organizational.company.status.inactive'
        ),
        value: 'INACTIVE',
      },
    ];
  }

  maxLengthParams(labelKey: string, requiredLength: number): Record<string, unknown> {
    return { fieldName: this.translateService.instant(labelKey), requiredLength };
  }

  isOverMaxLength(value: string | null | undefined): boolean {
    return !!value && value.length > this.maxFieldLength;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onConfirm(): void {
    const { code, name, registeredBusinessName, legalPerson, businessLicense } =
      this.formData;
    if (
      [code, name, registeredBusinessName, legalPerson, businessLicense].some(
        (v) => this.isOverMaxLength(v),
      )
    ) {
      return;
    }
    if (
      !this.formData.code ||
      !this.formData.name ||
      !this.formData.registeredBusinessName
    ) {
      this.showErrors.set(true);
      return;
    }

    this.submitting.set(true);
    this.serverError.set(null);

    this.config.data.onSubmit({
      data: this.formData,
      onSuccess: () => {
        this.submitting.set(false);
        this.dialogRef.close(true);
      },
      onError: (message: string) => {
        this.submitting.set(false);
        this.serverError.set({ message });
      },
    });
  }

  showServerError(message: string): void {
    this.submitting.set(false);
    this.serverError.set({ message });
  }

  closeOnSuccess(): void {
    this.dialogRef.close(true);
  }
}
